import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface InvoiceData {
  id: string;
  invoiceNumber: string;
  title: string;
  description: string;
  amount: string;
  currency: string;
  dueDate: string;
  status: string;
  nonMemberName: string;
  nonMemberEmail: string;
  nonMemberCompany?: string;
}

interface PaymentFormData {
  cardholderName: string;
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  billingZip: string;
}

export default function PublicInvoicePayment() {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState<PaymentFormData>({
    cardholderName: '',
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    billingZip: ''
  });
  const [formErrors, setFormErrors] = useState<Partial<PaymentFormData>>({});

  useEffect(() => {
    if (token) {
      fetchInvoice();
    }
  }, [token]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/invoices/public/${token}`);
      const result = await response.json();

      if (result.success) {
        setInvoice(result.invoice);
      } else {
        setError(result.message || 'Invoice not found or already paid');
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
      setError('Failed to load invoice. Please check the link and try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/.{1,4}/g);
    return match ? match.join(' ') : cleaned;
  };

  const handleInputChange = (field: keyof PaymentFormData, value: string) => {
    let formattedValue = value;

    if (field === 'cardNumber') {
      formattedValue = formatCardNumber(value);
      if (formattedValue.replace(/\s/g, '').length > 16) return;
    } else if (field === 'cvv') {
      formattedValue = value.replace(/\D/g, '');
      if (formattedValue.length > 4) return;
    } else if (field === 'expiryMonth' || field === 'expiryYear') {
      formattedValue = value.replace(/\D/g, '');
    } else if (field === 'billingZip') {
      formattedValue = value.replace(/[^0-9]/g, '');
      if (formattedValue.length > 5) return;
    }

    setFormData(prev => ({ ...prev, [field]: formattedValue }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<PaymentFormData> = {};

    if (!formData.cardholderName.trim()) {
      errors.cardholderName = 'Cardholder name is required';
    }

    const cardNum = formData.cardNumber.replace(/\s/g, '');
    if (!cardNum) {
      errors.cardNumber = 'Card number is required';
    } else if (cardNum.length < 13 || cardNum.length > 16) {
      errors.cardNumber = 'Invalid card number';
    }

    if (!formData.expiryMonth) {
      errors.expiryMonth = 'Required';
    } else {
      const month = parseInt(formData.expiryMonth);
      if (month < 1 || month > 12) {
        errors.expiryMonth = 'Invalid month';
      }
    }

    if (!formData.expiryYear) {
      errors.expiryYear = 'Required';
    } else {
      const year = parseInt(formData.expiryYear);
      const currentYear = new Date().getFullYear();
      if (year < currentYear || year > currentYear + 20) {
        errors.expiryYear = 'Invalid year';
      }
    }

    if (!formData.cvv) {
      errors.cvv = 'Required';
    } else if (formData.cvv.length < 3) {
      errors.cvv = 'Invalid CVV';
    }

    if (!formData.billingZip) {
      errors.billingZip = 'Required';
    } else if (formData.billingZip.length !== 5) {
      errors.billingZip = 'Invalid ZIP';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setPaying(true);
    setError('');

    try {
      // TODO: Integrate with Stripe here
      // For now, just simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 1500));

      const response = await fetch(`/api/invoices/public/${token}/pay`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          paymentMethod: 'card',
          lastFourDigits: formData.cardNumber.replace(/\s/g, '').slice(-4)
        })
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(true);
        setInvoice(prev => prev ? { ...prev, status: 'paid' } : null);
      } else {
        setError(result.message || 'Payment failed. Please try again.');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('An error occurred while processing payment. Please try again.');
    } finally {
      setPaying(false);
    }
  };

  const formatCurrency = (amount: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(parseFloat(amount));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isOverdue = (dueDate: string) => {
    return new Date(dueDate) < new Date();
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 15 }, (_, i) => currentYear + i);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-ggaa-light to-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-ggaa-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error && !invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-ggaa-light to-white p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="mb-6">
            <div className="mx-auto bg-red-100 rounded-full p-4 w-20 h-20 flex items-center justify-center">
              <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Invoice Not Found</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-ggaa-light to-white p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="mb-6">
            <div className="mx-auto bg-green-100 rounded-full p-4 w-20 h-20 flex items-center justify-center">
              <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Payment Recorded! ✅</h2>
          <p className="text-gray-600 mb-2">
            Thank you for your payment.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            Invoice #{invoice?.invoiceNumber} has been marked as paid. You will receive a confirmation email shortly.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            Note: This is a placeholder. Actual payment processing will be integrated with Stripe in the future.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (!invoice) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-ggaa-light to-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src="/images/ggaa-logo.png" 
                alt="GGAA Logo" 
                className="h-12 w-auto mr-4"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div>
                <h1 className="text-2xl font-bold text-ggaa-dark font-ranch">God's Green Acre Association</h1>
                <p className="text-sm text-gray-600">Secure Invoice Payment</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-ggaa-primary hover:text-ggaa-dark font-medium"
            >
              Member Login →
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Invoice Details */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Summary</h3>
              
              <div className="space-y-3 mb-6">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Invoice Number</p>
                  <p className="font-semibold text-gray-900">#{invoice.invoiceNumber}</p>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Billed To</p>
                  <p className="font-semibold text-gray-900">{invoice.nonMemberName}</p>
                  {invoice.nonMemberCompany && (
                    <p className="text-sm text-gray-600">{invoice.nonMemberCompany}</p>
                  )}
                </div>

                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Description</p>
                  <p className="text-sm text-gray-900">{invoice.title}</p>
                  {invoice.description && (
                    <p className="text-xs text-gray-600 mt-1">{invoice.description}</p>
                  )}
                </div>

                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Due Date</p>
                  <p className={`text-sm font-medium ${isOverdue(invoice.dueDate) ? 'text-red-600' : 'text-gray-900'}`}>
                    {formatDate(invoice.dueDate)}
                    {isOverdue(invoice.dueDate) && invoice.status !== 'paid' && (
                      <span className="ml-2 text-red-600 font-semibold">⚠️ OVERDUE</span>
                    )}
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">Total Amount</span>
                  <span className="text-2xl font-bold text-ggaa-primary">
                    {formatCurrency(invoice.amount)}
                  </span>
                </div>
              </div>

              <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <div>
                    <p className="text-xs font-medium text-green-800">Secure Payment</p>
                    <p className="text-xs text-green-700 mt-0.5">Your payment information is encrypted and secure</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Payment Form */}
          <div className="lg:col-span-2">
            {invoice.status === 'paid' ? (
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="text-center">
                  <div className="mx-auto bg-green-100 rounded-full p-4 w-20 h-20 flex items-center justify-center mb-6">
                    <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Invoice Paid</h2>
                  <p className="text-gray-600">This invoice has already been paid. Thank you!</p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment Information</h2>

                {/* Error Message */}
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <form onSubmit={handlePayment} className="space-y-6">
                  {/* Cardholder Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cardholder Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.cardholderName}
                      onChange={(e) => handleInputChange('cardholderName', e.target.value)}
                      placeholder="John Doe"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-ggaa-primary ${
                        formErrors.cardholderName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.cardholderName && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.cardholderName}</p>
                    )}
                  </div>

                  {/* Card Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Card Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.cardNumber}
                        onChange={(e) => handleInputChange('cardNumber', e.target.value)}
                        placeholder="1234 5678 9012 3456"
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-ggaa-primary ${
                          formErrors.cardNumber ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex space-x-1">
                        <svg className="h-6 w-auto text-gray-400" viewBox="0 0 36 24" fill="none">
                          <rect width="36" height="24" rx="3" fill="currentColor" fillOpacity="0.2"/>
                        </svg>
                      </div>
                    </div>
                    {formErrors.cardNumber && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.cardNumber}</p>
                    )}
                  </div>

                  {/* Expiry and CVV */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Month <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.expiryMonth}
                        onChange={(e) => handleInputChange('expiryMonth', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-ggaa-primary ${
                          formErrors.expiryMonth ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="">MM</option>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                          <option key={month} value={month.toString().padStart(2, '0')}>
                            {month.toString().padStart(2, '0')}
                          </option>
                        ))}
                      </select>
                      {formErrors.expiryMonth && (
                        <p className="mt-1 text-xs text-red-600">{formErrors.expiryMonth}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Year <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.expiryYear}
                        onChange={(e) => handleInputChange('expiryYear', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-ggaa-primary ${
                          formErrors.expiryYear ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="">YYYY</option>
                        {years.map(year => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                      {formErrors.expiryYear && (
                        <p className="mt-1 text-xs text-red-600">{formErrors.expiryYear}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CVV <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.cvv}
                        onChange={(e) => handleInputChange('cvv', e.target.value)}
                        placeholder="123"
                        maxLength={4}
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-ggaa-primary ${
                          formErrors.cvv ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {formErrors.cvv && (
                        <p className="mt-1 text-xs text-red-600">{formErrors.cvv}</p>
                      )}
                    </div>
                  </div>

                  {/* Billing ZIP */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Billing ZIP Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.billingZip}
                      onChange={(e) => handleInputChange('billingZip', e.target.value)}
                      placeholder="12345"
                      maxLength={5}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-ggaa-primary ${
                        formErrors.billingZip ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.billingZip && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.billingZip}</p>
                    )}
                  </div>

                  {/* Placeholder Notice */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Payment Integration Coming Soon</p>
                        <p className="text-xs text-blue-700">
                          Stripe payment processing will be integrated soon. For now, this form validates your information and records your payment intent.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={paying}
                    className="w-full bg-ggaa-primary hover:bg-ggaa-dark text-white font-semibold py-4 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-lg"
                  >
                    {paying ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing Payment...
                      </>
                    ) : (
                      <>🔒 Pay {formatCurrency(invoice.amount)}</>
                    )}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-8 text-center text-xs text-gray-500">
          <p>Questions about this invoice? Contact us at <a href="mailto:billing@ggaa.org" className="text-ggaa-primary hover:text-ggaa-dark">billing@ggaa.org</a></p>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-gray-600">
          <p>© 2024 God's Green Acre Association. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
} 